import React from "react";
import { Link } from "react-router-dom";
import { IndianRupee, MapPin, Users } from "lucide-react";
import { useApp } from "@/state/AppContext";
import { localized } from "@/theme/tenantTheme";
import { FavoriteButton } from "@/components/FavoriteButton";
import { rupees } from "@/lib/format";

export function payText(job, t) {
  // P2-004: `job.salary_max` is a wrapper object ({minor_units}), always truthy even when
  // `minor_units` is 0/unset — checking the object itself (not `.minor_units`) took the range
  // branch for an open-ended salary and rendered "₹8,000 – ₹0", which reads as a data error.
  const minMinor = job.salary_min?.minor_units;
  const maxMinor = job.salary_max?.minor_units;
  const hasMin = typeof minMinor === "number" && minMinor > 0;
  const hasMax = typeof maxMinor === "number" && maxMinor > 0;
  if (!hasMin && !hasMax) return "";
  let amount;
  if (hasMin && hasMax && maxMinor !== minMinor) {
    amount = `${rupees({ minor_units: minMinor })} – ${rupees({ minor_units: maxMinor })}`;
  } else if (hasMin) {
    amount = hasMax && maxMinor === minMinor ? rupees({ minor_units: minMinor }) : t("jobs.fromPay", { amount: rupees({ minor_units: minMinor }) });
  } else {
    amount = rupees({ minor_units: maxMinor });
  }
  return `${amount} / ${t(`jobs.periods.${job.salary_period}`)}`;
}

export function JobCard({ job }) {
  const { lang, t } = useApp();
  const title = localized(lang, job, "title");
  const pay = payText(job, t);
  return (
    <Link
      to={`/jobs/${job.id}`}
      data-testid={`job-card-${job.id}`}
      className="group relative flex flex-col gap-3 rounded-2xl border border-border bg-card p-5 pr-12 transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg"
    >
      <FavoriteButton targetType="job" targetId={job.id} className="absolute right-3 top-3" />
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-heading text-lg font-semibold leading-snug">{title}</h3>
        <span className="shrink-0 rounded-full border border-border px-3 py-1 text-xs font-medium text-muted-foreground">
          {t(`jobs.types.${job.job_type}`)}
        </span>
      </div>
      {job.employer_name && <p className="text-sm text-muted-foreground">{job.employer_name}</p>}
      <div className="mt-auto flex flex-wrap gap-3 pt-2 text-xs text-muted-foreground">
        {pay && (
          <span className="flex items-center gap-1 font-medium text-foreground">
            <IndianRupee size={12} /> {pay}
          </span>
        )}
        {job.area && (
          <span className="flex items-center gap-1">
            <MapPin size={12} /> {job.area}
          </span>
        )}
        {job.openings > 0 && (
          <span className="flex items-center gap-1">
            <Users size={12} /> {t("jobs.openings", { count: job.openings })}
          </span>
        )}
      </div>
    </Link>
  );
}
